/**
 * Shared cart state — one source of truth for quantity, page-wide.
 *
 * Ported from raftor_webhq/public/js/listing_quantity.js, with the central
 * change the plan calls for: quantity is server-authoritative and PAGE-GLOBAL,
 * not per-card. The same item can appear in a carousel and a listing on one
 * page; today each card reconciles itself against a shared snapshot by walking
 * the DOM. Here the store holds the numbers and notifies subscribers, and the
 * grid re-renders through the renderer. Nothing reaches into a card.
 *
 * Preserved verbatim because it is load-bearing:
 *  - 150ms debounce with in-flight + queued guards (a burst of updates makes
 *    one request, and a request arriving during another is not dropped)
 *  - snapshot endpoint with fallback to the full cart quotation
 *  - refresh on 'cart-updated', pageshow, and visibilitychange
 *  - syncedQty / pendingUpdate dirty tracking, so a typed quantity that has
 *    not reached the server is flushed before navigating away (see flush())
 */

const SNAPSHOT_METHOD = 'raftor_webhq.checkout.simple_checkout.get_cart_items_snapshot';
const FALLBACK_METHOD = 'webshop.webshop.shopping_cart.cart.get_cart_quotation';
// Core webshop's OWN mutation, called from its bind_add_to_cart_action()
// binder outside this store's control (see optimisticAdd()'s own comment) --
// listen() below watches for this exact request completing so a refresh()
// can be triggered by the real event rather than a guessed delay.
const UPDATE_CART_METHOD = 'webshop.webshop.shopping_cart.cart.update_cart';
const DEBOUNCE_MS = 150;

function frappeCall(options) {
	return new Promise((resolve) => {
		if (typeof frappe === 'undefined' || !frappe.call) {
			resolve(null);
			return;
		}

		let settled = false;
		const done = (value) => {
			if (settled) return;
			settled = true;
			resolve(value);
		};

		const req = frappe.call(
			Object.assign({}, options, {
				callback: (r) => done(r && r.message),
				error: () => done(null),
			})
		);

		// Same trap as in webshop_source.js: frappe's deferred fires `always`
		// BEFORE opts.callback, so this net has to be deferred or it would
		// resolve null over every successful cart snapshot.
		if (req && typeof req.always === 'function') {
			req.always(() => setTimeout(() => done(null), 0));
		}
	});
}

export class CartStore {
	constructor() {
		this._qtyByItemCode = new Map();
		this._subscribers = new Set();
		this._pending = new Map(); // itemCode -> qty not yet confirmed by the server
		this._inFlight = false;
		this._queued = false;
		this._timer = null;
		this._listening = false;
		// itemCodes with a pending value from optimisticAdd() below, as
		// opposed to a real in-flight setQty() edit -- see that method's own
		// comment for why these need different cleanup.
		this._optimistic = new Set();
	}

	/** @returns {number} confirmed-or-pending quantity for an item */
	getQty(itemCode) {
		if (!itemCode) return 0;
		if (this._pending.has(itemCode)) return this._pending.get(itemCode);
		return this._qtyByItemCode.get(itemCode) || 0;
	}

	/** True when a locally-set quantity has not yet been confirmed. */
	isPending(itemCode) {
		return this._pending.has(itemCode);
	}

	subscribe(fn) {
		this._subscribers.add(fn);
		return () => this._subscribers.delete(fn);
	}

	_notify(changedCodes) {
		for (const fn of this._subscribers) {
			try {
				fn(changedCodes);
			} catch (err) {
				// A broken subscriber must not stop the others from updating.
				console.error('[rfhq cart store] subscriber failed', err);
			}
		}
	}

	/**
	 * Optimistically set a quantity, then push it to the server.
	 * The UI updates immediately; the refresh that follows reconciles.
	 */
	async setQty(itemCode, qty) {
		if (!itemCode) return;
		const next = Math.max(0, Number(qty) || 0);

		this._pending.set(itemCode, next);
		this._notify([itemCode]);

		const shoppingCart =
			(typeof window !== 'undefined' && window.shopping_cart) ||
			(typeof webshop !== 'undefined' && webshop.webshop && webshop.webshop.shopping_cart);

		if (!shoppingCart || typeof shoppingCart.update_cart !== 'function') {
			this._pending.delete(itemCode);
			this._notify([itemCode]);
			return;
		}

		const result = await new Promise((resolve) => {
			shoppingCart.update_cart({
				item_code: itemCode,
				qty: next,
				with_items: 0,
				callback: (r) => resolve(r),
			});
		});

		this._pending.delete(itemCode);

		if (result && result.exc) {
			// Server refused — drop the optimistic value and re-show the truth.
			this._notify([itemCode]);
			return;
		}

		this._qtyByItemCode.set(itemCode, next);
		this._notify([itemCode]);

		if (typeof window !== 'undefined' && window.jQuery) {
			window.jQuery(document).trigger('cart-updated', [{ item_code: itemCode, qty: next }]);
		}
		this.refresh();
	}

	/**
	 * Instant, no-network stepper appearance for a plain "Add to Cart" click.
	 * That click's actual server call is core webshop's own
	 * bind_add_to_cart_action() (a different code path this store does not
	 * own) -- setQty() would fire a SECOND, duplicate update_cart request.
	 * This only flips the UI to the stepper immediately; listen()'s
	 * ajaxComplete hook calls refresh() the moment that real request lands,
	 * which reconciles this guess with whatever qty the server actually
	 * recorded (see _applySnapshot's handling of `_optimistic`). Found live:
	 * without this, a shopper watched the button do nothing for the entire
	 * add-to-cart round trip before the stepper ever appeared.
	 */
	optimisticAdd(itemCode, qty) {
		if (!itemCode || this._qtyByItemCode.get(itemCode) > 0) return;
		this._pending.set(itemCode, qty || 1);
		this._optimistic.add(itemCode);
		this._notify([itemCode]);
	}

	/**
	 * Flush a locally-set quantity before navigating.
	 * Returns a promise that settles once the server has the value — callers
	 * that are about to change location should await it, or a typed quantity
	 * is silently lost.
	 */
	async flush(itemCode) {
		if (!this._pending.has(itemCode)) return;
		await this.setQty(itemCode, this._pending.get(itemCode));
	}

	/** Debounced reload of the whole cart snapshot. */
	refresh() {
		if (this._timer) clearTimeout(this._timer);
		this._timer = setTimeout(() => this._doRefresh(), DEBOUNCE_MS);
	}

	async _doRefresh() {
		if (this._inFlight) {
			this._queued = true;
			return;
		}
		this._inFlight = true;

		try {
			let items = null;

			const snapshot = await frappeCall({ type: 'GET', method: SNAPSHOT_METHOD });
			if (snapshot && snapshot.items) {
				items = snapshot.items;
			} else {
				const quotation = await frappeCall({ type: 'GET', method: FALLBACK_METHOD });
				if (quotation && quotation.doc && Array.isArray(quotation.doc.items)) {
					items = quotation.doc.items;
				}
			}

			if (items) this._applySnapshot(items);
		} finally {
			this._inFlight = false;
			if (this._queued) {
				this._queued = false;
				this.refresh();
			}
		}
	}

	_applySnapshot(items) {
		const next = new Map();
		for (const row of items) {
			if (!row || !row.item_code) continue;
			next.set(row.item_code, parseFloat(row.qty || 0) || 0);
		}

		// An optimisticAdd() guess is superseded by this snapshot regardless
		// of whether the item actually shows up in it -- either the add
		// landed (the real qty is now known) or it silently failed (the
		// guess must revert to 0), and this is the first authoritative data
		// to arrive since the guess was made. A setQty()-driven pending edit
		// is untouched here: it clears its own entry the moment ITS OWN
		// update_cart call resolves, independent of any refresh.
		//
		// Seeded into `changed` unconditionally (a Set, not pushed onto the
		// two loops below) -- an item whose add silently failed never
		// appears in `next` AND was never a real key of the old
		// `_qtyByItemCode` (it only ever lived in `_pending`), so neither
		// loop below would otherwise notice it needs to revert to 0.
		const changed = new Set();
		for (const code of this._optimistic) {
			this._pending.delete(code);
			changed.add(code);
		}
		this._optimistic.clear();

		for (const [code, qty] of next) {
			if (this._qtyByItemCode.get(code) !== qty) changed.add(code);
		}
		for (const code of this._qtyByItemCode.keys()) {
			if (!next.has(code)) changed.add(code);
		}

		this._qtyByItemCode = next;
		if (changed.size) this._notify(Array.from(changed));
	}

	/** Attach the page-level refresh triggers. Idempotent. */
	listen() {
		if (this._listening || typeof window === 'undefined') return;
		this._listening = true;

		if (window.jQuery) {
			window.jQuery(document).on('cart-updated', () => this.refresh());

			// Core's own $.ajax calls (frappe.call is built on it -- see
			// frappe/request.js's own ajaxSend/ajaxComplete counter) fire this
			// globally regardless of which code triggered them. Used to catch
			// core's bind_add_to_cart_action() completing its update_cart
			// call, a request this store has no other hook into (that binder
			// is core webshop's, not this file's) -- the real signal an
			// optimisticAdd() guess is safe to reconcile against, in place of
			// a fixed delay guessing how long that request usually takes.
			window.jQuery(document).ajaxComplete((event, xhr, settings) => {
				if (settings && settings.url && settings.url.indexOf(UPDATE_CART_METHOD) !== -1) {
					this.refresh();
				}
			});
		}
		window.addEventListener('pageshow', () => this.refresh());
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') this.refresh();
		});
	}
}

/** Page-wide singleton — the point of the store is that there is only one. */
export const cartStore = new CartStore();
